import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const API_BASE = "/api/repricing";

const money = (value) => {
  if (value === null || value === undefined || value === "") return "Unavailable";
  const n = Number(value || 0);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(value);
};

const numberText = (value) => {
  if (value === null || value === undefined || value === "") return "Unavailable";
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : String(value);
};

const deltaText = (delta) => {
  if (!delta || delta.absolute === null || delta.absolute === undefined) return "No comparison";
  const prefix = Number(delta.absolute) > 0 ? "+" : "";
  const pct = delta.percent === null || delta.percent === undefined ? "" : ` (${prefix}${delta.percent.toFixed(2)}%)`;
  return `${prefix}${delta.absolute}${pct}`;
};

function WindowPanel({ title, data, kind }) {
  const available = !!data?.available;
  const current = data?.current_7d || {};
  const previous = data?.previous_7d || {};
  const delta = data?.delta || {};

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Chip size="small" color={available ? "success" : "default"} label={available ? "Available" : "Unavailable"} />
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Current 7 days
            </Typography>
            <Typography variant="body2">
              {kind === "sales"
                ? `${numberText(current.units)} units / ${money(current.revenue)}`
                : `${money(current.spend)} spend / ${money(current.revenue)} revenue`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Previous 7 days
            </Typography>
            <Typography variant="body2">
              {kind === "sales"
                ? `${numberText(previous.units)} units / ${money(previous.revenue)}`
                : `${money(previous.spend)} spend / ${money(previous.revenue)} revenue`}
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary">
          Change: {kind === "sales" ? deltaText(delta.units) : deltaText(delta.spend)}
        </Typography>
      </Stack>
    </Paper>
  );
}

export default function RepricingStoreSignals() {
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadSignals = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/store-signals`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load Store Signals");
      }
      setSignals(data);
    } catch (e) {
      setSignals(null);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  const blockerRows = useMemo(
    () => (Array.isArray(signals?.candidate_blockers) ? signals.candidate_blockers : []),
    [signals]
  );
  const listingCounts = signals?.listing_counts || {};
  const poCounts = signals?.po_counts || {};
  const dataHealth = signals?.ebay_data_health || {};
  const warnings = Array.isArray(signals?.warnings) ? signals.warnings : [];

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Store Signals
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only sales windows, sync health, PO maturity, and candidate blockers from existing eBay intelligence data.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={loadSignals} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {warnings.map((warning) => (
        <Alert key={warning} severity="warning">
          {warning}
        </Alert>
      ))}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <WindowPanel title="Sales and revenue" data={signals?.sales_windows} kind="sales" />
        </Grid>
        <Grid item xs={12} md={6}>
          <WindowPanel title="Ad performance" data={signals?.ad_windows} kind="ads" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Listing Counts
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${numberText(listingCounts.active_listing_count)} active`} />
              <Chip label={`${numberText(listingCounts.stale_listing_count)} stale`} />
              <Chip label={`${numberText(listingCounts.total_listing_count)} total`} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              PO Maturity
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${numberText(poCounts.mature_po_count)} mature POs`} />
              <Chip label={`${numberText(poCounts.po_linked_listing_count)} PO-linked listings`} />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              eBay Data Health
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip color="warning" label={`${numberText(dataHealth.stale_sync_count)} stale sync`} />
              <Chip label={`${numberText(dataHealth.missing_sync_count)} missing sync`} />
              <Chip color="warning" label={`${numberText(dataHealth.failed_sync_count)} failed sync`} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Candidate Blockers
        </Typography>
        {blockerRows.length ? (
          <Grid container spacing={1}>
            {blockerRows.map((blocker) => (
              <Grid item xs={12} md={6} key={blocker.key}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Chip size="small" color="warning" label={numberText(blocker.count)} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {blocker.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {blocker.detail}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No candidate blockers returned by the read-only signal check.
          </Typography>
        )}
      </Paper>
    </Stack>
  );
}
